import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { Pencil, X, Trash2, ShoppingCart, ArrowUpDown, RotateCcw, Filter, ChevronRight, MapPin } from "lucide-react";
import { translateText } from "../utils/categoryTranslations";

const MyDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const { user, accessToken, language } = useContext(AuthContext);
  const navigate = useNavigate();

  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterCity, setFilterCity] = useState("");
  const [filterArea, setFilterArea] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterHasPhoto, setFilterHasPhoto] = useState("all");
  const [filterPriceMin, setFilterPriceMin] = useState("");
  const [filterPriceMax, setFilterPriceMax] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterOptions, setFilterOptions] = useState({ categories: [], cities: [], areas: [] });

  // Sorting
  const [sortVisible, setSortVisible] = useState(false);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [refreshFlag, setRefreshFlag] = useState(false);

  const translations = {
    el: {
      title: "Οι προσφορές μου",
      filter: "Φίλτρα",
      sort: "Ταξινόμηση",
      offer: "Προσφορά",
      datePub: "Ημερομηνία δημοσίευσης",
      status: "Κατάσταση",
      store: "Όνομα καταστήματος",
      rating: "Βαθμολογία",
      actions: "Ενέργειες",
      category: "Κατηγορία",
      city: "Πόλη",
      area: "Περιοχή",
      selectCat: "Επιλογή κατηγορίας",
      selectCity: "Επιλογή πόλης",
      selectArea: "Επιλογή περιοχής",
      sortBy: "Ταξινόμηση κατά",
      name: "Όνομα προσφοράς",
      price: "Τιμή",
      asc: "Αύξουσα",
      desc: "Φθίνουσα",
      az: "Α-Ω",
      za: "Ω-Α",
      noOffers: "Δεν έχετε δημοσιεύσει προσφορές ακόμα.",
      delTitle: "Διαγραφή Προσφοράς",
      delMsg: "Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την προσφορά;",
      cancel: "Ακύρωση",
      delete: "Διαγραφή",
      edit: "Επεξεργασία",
      location: "Όνομα καταστήματος",
      statusActive: "Ενεργή",
      statusExpired: "Ληγμένη",
      deactivateOffer: "Απενεργοποίηση προσφοράς",
      activateOffer: "Ενεργοποίηση προσφοράς",
      loadMore: "Φόρτωση περισσότερων...",
      nmDiscounts: "Δεν υπάρχουν άλλες προσφορές",
      statusFilter: "Κατάσταση προσφοράς",
      allStatuses: "Όλες",
      onlyActive: "Μόνο ενεργές",
      onlyExpired: "Μόνο ληγμένες",
      photoFilter: "Φωτογραφία",
      allPhotos: "Όλες",
      withPhoto: "Με φωτογραφία",
      withoutPhoto: "Χωρίς φωτογραφία",
      priceRange: "Εύρος τιμής",
      minPrice: "Από (€)",
      maxPrice: "Έως (€)",
      dateRange: "Εύρος ημερομηνιών",
      dateFrom: "Από",
      dateTo: "Έως"
    },
    en: {
      title: "My offer list",
      filter: "Filter",
      sort: "Sort",
      offer: "Offer",
      datePub: "Date published",
      status: "Status",
      store: "Store name",
      rating: "Rating",
      actions: "Actions",
      category: "Category",
      city: "City",
      area: "Area",
      selectCat: "Select category",
      selectCity: "Select city",
      selectArea: "Select area",
      sortBy: "Sort by",
      name: "Offer name",
      price: "Price",
      asc: "Ascending",
      desc: "Descending",
      az: "A-Z",
      za: "Z-A",
      noOffers: "No offers posted yet.",
      delTitle: "Delete offer",
      delMsg: "Are you sure you want to delete this offer?",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      location: "Store name",
      statusActive: "Active",
      statusExpired: "Expired",
      deactivateOffer: "Deactivate offer",
      activateOffer: "Activate offer",
      loadMore: "Loading more...",
      nmDiscounts: "No more offers",
      statusFilter: "Offer status",
      allStatuses: "All",
      onlyActive: "Active only",
      onlyExpired: "Expired only",
      photoFilter: "Photo",
      allPhotos: "All",
      withPhoto: "With photo",
      withoutPhoto: "Without photo",
      priceRange: "Price range",
      minPrice: "From (€)",
      maxPrice: "To (€)",
      dateRange: "Date range",
      dateFrom: "From",
      dateTo: "To"
    }
  };

  const t = translations[language] || translations.el;

  const isAlphabeticalSortField = (field) => ["title", "status", "store_name"].includes(field);
  const getOrderLabel = (value) => {
    if (isAlphabeticalSortField(sortField)) {
      return value === "asc" ? t.az : t.za;
    }
    return value === "asc" ? t.asc : t.desc;
  };

  const [deletePopup, setDeletePopup] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState(null);

  const getStatusLabel = (status) => {
    if (status === "Active") return t.statusActive;
    if (status === "Expired") return t.statusExpired;
    return status;
  };

  useEffect(() => {
    const fetchDiscounts = async (pageToLoad = 1) => {
      if (!user || !accessToken) return;
      try {
        if (pageToLoad === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const offset = (pageToLoad - 1) * pageSize;
        const params = new URLSearchParams({
          sortField,
          sortOrder,
          lang: language,
          limit: pageSize,
          offset
        });
        if (filterCategory.length > 0) params.append("categories", filterCategory.join(","));
        if (filterCity) params.append("city", filterCity);
        if (filterArea.length > 0) params.append("areas", filterArea.join(","));
        if (filterStatus !== "all") params.append("status", filterStatus);
        if (filterHasPhoto !== "all") params.append("hasPhoto", filterHasPhoto);
        if (filterPriceMin !== "") params.append("priceMin", filterPriceMin);
        if (filterPriceMax !== "") params.append("priceMax", filterPriceMax);
        if (filterDateFrom) params.append("dateFrom", filterDateFrom);
        if (filterDateTo) params.append("dateTo", filterDateTo);

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/mydiscounts/mydiscounts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await response.json();

        const fetched = data.mydiscounts || [];
        if (pageToLoad === 1) {
          setDiscounts(fetched);
        } else {
          setDiscounts(prev => [...prev, ...fetched]);
        }

        setHasMore(fetched.length === pageSize);
        if (pageToLoad === 1) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    if (user && accessToken) {
      setDiscounts([]);
      setPage(1);
      setHasMore(true);
      fetchDiscounts(1);
    }
  }, [user, accessToken, sortField, sortOrder, refreshFlag, filterCategory, filterCity, filterArea, filterStatus, filterHasPhoto, filterPriceMin, filterPriceMax, filterDateFrom, filterDateTo, language]);

  useEffect(() => {
    const fetchDiscounts = async (pageToLoad = 1) => {
      if (!user || !accessToken) return;
      try {
        if (pageToLoad === 1) {
          return;
        }

        setLoadingMore(true);
        const offset = (pageToLoad - 1) * pageSize;
        const params = new URLSearchParams({
          sortField,
          sortOrder,
          lang: language,
          limit: pageSize,
          offset
        });
        if (filterCategory.length > 0) params.append("categories", filterCategory.join(","));
        if (filterCity) params.append("city", filterCity);
        if (filterArea.length > 0) params.append("areas", filterArea.join(","));
        if (filterStatus !== "all") params.append("status", filterStatus);
        if (filterHasPhoto !== "all") params.append("hasPhoto", filterHasPhoto);
        if (filterPriceMin !== "") params.append("priceMin", filterPriceMin);
        if (filterPriceMax !== "") params.append("priceMax", filterPriceMax);
        if (filterDateFrom) params.append("dateFrom", filterDateFrom);
        if (filterDateTo) params.append("dateTo", filterDateTo);

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/mydiscounts/mydiscounts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await response.json();

        const fetched = data.mydiscounts || [];
        setDiscounts(prev => [...prev, ...fetched]);
        setHasMore(fetched.length === pageSize);
        setLoadingMore(false);
      } catch (error) {
        console.error(error);
        setLoadingMore(false);
      }
    };

    if (!user || !accessToken) return;
    if (page === 1) return;
    fetchDiscounts(page);
  }, [page, user, accessToken]);

  useEffect(() => {
    const fetchOptions = async () => {
      if (!accessToken) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/mydiscounts/filter-options?lang=${language}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.status === 401) { console.error("Unauthorized! Check your token."); return; }
        const json = await res.json();
        setFilterOptions({
          categories: json.categories || [],
          cities: json.cities || [],
          areas: []
        });
      } catch (err) { console.error(err); }
    };
    fetchOptions();
  }, [language, accessToken]);

  //Fetch Areas when City changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (!filterCity || !accessToken) { setFilterOptions(prev => ({ ...prev, areas: [] })); return; }
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/discounts/areasByCity?city=${filterCity}&lang=${language}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const json = await res.json();
        setFilterOptions(prev => ({ ...prev, areas: json.areas || [] }));
      } catch (err) { console.error(err); }
    };
    fetchAreas();
  }, [filterCity, language, accessToken]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((prev) => prev + 1);
      }
    }, {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore]);
  const toggleSelection = (value, state, setState) => {
    state.includes(value) ? setState(state.filter(v => v !== value)) : setState([...state, value]);
  };

  const toggleExpand = (id) => setExpandedRow(expandedRow === id ? null : id);

  const handleEdit = (item) => {
    navigate(`/editdiscount/${item.discount_id}`, { state: { discount: item } });
  };

  const handleShowOnMap = (item) => {
    if (!item.full_store?.latitude) { alert("No location data"); return; }
    const s = item.full_store;
    navigate("/showonmap", {
      state: {
        discountTitle: item.title,
        storeName: language === 'el' ? s.store_name_el : s.store_name_en,
        lat: s.latitude, lng: s.longitude,
        city: language === 'el' ? s.city_el : s.city_en,
        area: language === 'el' ? s.area_el : s.area_en,
        road: language === 'el' ? s.road_el : s.road_en
      }
    });
  };

  const handleExpire = async (discountId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/mydiscounts/expire/${discountId}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${accessToken}`
        }
      });
      if (!response.ok) throw new Error("Failed to toggle status");
      setRefreshFlag(prev => !prev);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/mydiscounts/delete/${discountToDelete}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      if (!response.ok) throw new Error("Failed to delete discount");

      setRefreshFlag(prev => !prev);
      setDeletePopup(false);
      setDiscountToDelete(null);

    } catch (err) {
      console.error(err);
    }
  };

  const handleViewDiscount = (discountId) => {
    navigate("/viewdiscount", {
      state: { discountId }
    });
  };

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-col md:flex-row relative bg-custom-background w-full">
        <ProfileSidebar />
        <main id="mydiscounts-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 xl:ml-20 text-center md:text-left">

          <div className="flex justify-between items-center mb-8 gap-6 w-full max-w-6xl mt-12 md:mt-0">
            <h2 className="text-2xl font-semibold text-custom-letters flex items-center gap-2">
              <ShoppingCart className="text-custom-grad1 shrink-0" size={22} />
              <span className="leading-tight">{t.title}</span>
            </h2>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button onClick={() => setFiltersVisible(!filtersVisible)} title={t.filter} className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                <Filter size={18} />
              </button>
              <button onClick={() => setSortVisible(!sortVisible)} title={t.sort} className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                <ArrowUpDown size={18} />
              </button>
            </div>
          </div>

          {/* FILTER PANEL */}
          {filtersVisible && (
            <div className="bg-custom-header p-6 rounded-xl shadow mb-6 w-full max-w-6xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 animate-in fade-in duration-300 border border-gray-100">
              {/* Category Filter */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.category}</p>
                <select
                  onChange={(e) => {
                    if (e.target.value) toggleSelection(e.target.value, filterCategory, setFilterCategory);
                    e.target.value = "";
                  }}
                  className="w-full p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1"
                >
                  <option value="">{t.selectCat}</option>
                  {filterOptions.categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {language === 'el' ? cat.category_name_el : cat.category_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.statusFilter}</p>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1"
                >
                  <option value="all">{t.allStatuses}</option>
                  <option value="Active">{t.onlyActive}</option>
                  <option value="Expired">{t.onlyExpired}</option>
                </select>
              </div>

              {/* City Filter - Single Selection */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.city}</p>
                <select
                  value={filterCity}
                  onChange={(e) => {
                    setFilterCity(e.target.value);
                    setFilterArea([]);
                  }}
                  className="w-full p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1"
                >
                  <option value="">{t.selectCity}</option>
                  {filterOptions.cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Area Filter - Multiple Selection */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.area}</p>
                <select
                  disabled={!filterCity}
                  onChange={(e) => {
                    if (e.target.value) toggleSelection(e.target.value, filterArea, setFilterArea);
                    e.target.value = "";
                  }}
                  className="w-full p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1 disabled:bg-gray-100"
                >
                  <option value="">{t.selectArea}</option>
                  {filterOptions.areas.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>

              {/* Photo Filter */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.photoFilter}</p>
                <select
                  value={filterHasPhoto}
                  onChange={(e) => setFilterHasPhoto(e.target.value)}
                  className="w-full p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1"
                >
                  <option value="all">{t.allPhotos}</option>
                  <option value="yes">{t.withPhoto}</option>
                  <option value="no">{t.withoutPhoto}</option>
                </select>
              </div>

              {/* Price Range Filter */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.priceRange}</p>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t.minPrice}
                    value={filterPriceMin}
                    onChange={(e) => setFilterPriceMin(e.target.value)}
                    className="w-1/2 p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t.maxPrice}
                    value={filterPriceMax}
                    onChange={(e) => setFilterPriceMax(e.target.value)}
                    className="w-1/2 p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1"
                  />
                </div>
              </div>

              {/* Date Range Filter */}
              <div className="sm:col-span-2 md:col-span-3">
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.dateRange}</p>
                <div className="flex gap-2 flex-wrap">
                  <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                    <label className="text-xs text-custom-letters">{t.dateFrom}</label>
                    <input
                      type="date"
                      value={filterDateFrom}
                      max={filterDateTo || undefined}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="p-2 rounded border text-sm w-full outline-none focus:ring-2 focus:ring-custom-grad1"
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                    <label className="text-xs text-custom-letters">{t.dateTo}</label>
                    <input
                      type="date"
                      value={filterDateTo}
                      min={filterDateFrom || undefined}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="p-2 rounded border text-sm w-full outline-none focus:ring-2 focus:ring-custom-grad1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PILLS SECTION */}
          <div className="flex flex-wrap gap-2 mt-3 mb-6 w-full max-w-6xl">
            {/* Category Pills */}
            {filterCategory.map(catId => {
              const catObj = filterOptions.categories?.find(c => String(c.category_id) === String(catId));
              if (!catObj) return null;
              return (
                <span key={catId} onClick={() => toggleSelection(catId, filterCategory, setFilterCategory)}
                  className="px-3 py-1 bg-custom-grad1 text-white rounded-full cursor-pointer text-sm">
                  {language === 'el' ? catObj.category_name_el : catObj.category_name}  ✕
                </span>
              );
            })}

            {/* City Pill */}
            {filterCity && (
              <span
                onClick={() => { setFilterCity(""); setFilterArea([]); }}
                className="px-3 py-1 bg-custom-grad3 text-white rounded-full cursor-pointer text-sm">
                {filterCity} ✕
              </span>
            )}

            {/* Area Pills */}
            {filterArea.map(area => (
              <span
                key={`area-${area}`}
                onClick={() => toggleSelection(area, filterArea, setFilterArea)}
                className="px-3 py-1 bg-custom-grad2 text-white rounded-full cursor-pointer text-sm">
                {area} ✕
              </span>
            ))}

            {/* Status pill */}
            {filterStatus !== "all" && (
              <span
                onClick={() => setFilterStatus("all")}
                className="px-3 py-1 bg-custom-grad4 text-white rounded-full cursor-pointer text-sm">
                {filterStatus === "Active" ? t.onlyActive : t.onlyExpired} ✕
              </span>
            )}

            {/* Photo pill */}
            {filterHasPhoto !== "all" && (
              <span
                onClick={() => setFilterHasPhoto("all")}
                className="px-3 py-1 bg-custom-grad1 text-white rounded-full cursor-pointer text-sm">
                {filterHasPhoto === "yes" ? t.withPhoto : t.withoutPhoto} ✕
              </span>
            )}

            {/* Price range pill */}
            {(filterPriceMin !== "" || filterPriceMax !== "") && (
              <span
                onClick={() => { setFilterPriceMin(""); setFilterPriceMax(""); }}
                className="px-3 py-1 bg-custom-grad2 text-white rounded-full cursor-pointer text-sm">
                €{filterPriceMin || "0"} – €{filterPriceMax || "∞"} ✕
              </span>
            )}

            {/* Date range pill */}
            {(filterDateFrom || filterDateTo) && (
              <span
                onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); }}
                className="px-3 py-1 bg-custom-grad3 text-white rounded-full cursor-pointer text-sm">
                {filterDateFrom || "…"} → {filterDateTo || "…"} ✕
              </span>
            )}
          </div>

          {sortVisible && (
            <div className="bg-custom-header p-4 rounded-xl shadow mb-6 w-full max-w-xs">
              <p className="text-sm font-semibold mb-2 text-custom-letters">{t.sortBy}</p>
              <select value={sortField} onChange={(e) => setSortField(e.target.value)} className="w-full p-2 rounded border mb-3">
                <option value="title">{t.name}</option>
                <option value="created_at">{t.datePub}</option>
                <option value="status">{t.status}</option>
                <option value="store_name">{t.store}</option>
                <option value="rating">{t.rating}</option>
                <option value="discount_price">{t.price}</option>
              </select>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="w-full p-2 rounded border">
                <option value="asc">{getOrderLabel("asc")}</option>
                <option value="desc">{getOrderLabel("desc")}</option>
              </select>
            </div>
          )}

          <div className="w-full max-w-6xl bg-white/40 rounded-2xl shadow-lg p-4 sm:p-6 backdrop-blur-sm">

            {/* Desktop Table */}
            <div className="hidden xl:block overflow-x-auto">
              <table className="min-w-full text-sm text-custom-letters text-center border-collapse">
                <thead className="border-b font-medium">
                  <tr>
                    <th className="px-4 py-3">{t.offer}</th>
                    <th className="px-4 py-3">{t.datePub}</th>
                    <th className="px-4 py-3">{t.status}</th>
                    <th className="px-4 py-3">{t.location}</th>
                    <th className="px-4 py-3">{t.rating}</th>
                    <th className="px-4 py-3">{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.length > 0 ? discounts.map(item => (
                    <tr key={item.discount_id} className="border-b hover:bg-custom-background transition">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleViewDiscount(item.discount_id)}
                          className="font-medium text-custom-grad4 hover:underline cursor-pointer text-left w-full"
                        >
                          {item.title}
                        </button>
                      </td>
                      <td className="px-4 py-3">{new Date(item.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-custom-grad1 font-semibold">{getStatusLabel(item.status)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleShowOnMap(item)}
                          className="text-custom-grad4 hover:underline cursor-pointer w-full"
                        >
                          <span className="flex items-center justify-center gap-1">
                            <MapPin size={13} /> {item.store_name}
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-3">{item.rating}/5</td>
                      <td className="px-4 py-3 flex justify-center gap-2">
                        <button onClick={() => handleEdit(item)} title={t.edit}
                          className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full"><Pencil size={18} /></button>
                        <button onClick={() => handleExpire(item.discount_id)}
                          title={item.status === "Active" ? t.deactivateOffer : t.activateOffer}
                          className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                          {item.status === "Active" ? <X size={18} /> : <RotateCcw size={18} />}
                        </button>
                        <button
                          onClick={() => {
                            setDiscountToDelete(item.discount_id);
                            setDeletePopup(true);
                          }}
                          title={t.delete}
                          className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full"><Trash2 size={18} /></button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="py-6 italic text-custom-letters">{t.noOffers}</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="block xl:hidden">
              {discounts.map(item => {
                const isExpanded = expandedRow === item.discount_id;

                return (
                  <div key={item.discount_id} className="border border-gray-300 rounded-xl mb-4 bg-custom-background/60 shadow-sm overflow-hidden">
                    <div className="flex items-center px-3 py-3 gap-2">

                      {/*(Chevron) */}
                      <button
                        onClick={() => toggleExpand(item.discount_id)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-transform duration-200 flex-shrink-0"
                        style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                      >
                        <ChevronRight size={20} className="text-gray-500" />
                      </button>

                      {/*(Navigation) */}
                      <div className="flex-1 min-w-0 text-left">
                        <button
                          onClick={() => handleViewDiscount(item.discount_id)}
                          className="block font-semibold text-custom-grad4 hover:underline cursor-pointer truncate text-sm text-left w-full"
                        >
                          {item.title}
                        </button>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => handleEdit(item)} title={t.edit} className="bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => handleExpire(item.discount_id)}
                          title={item.status === "Active" ? t.deactivateOffer : t.activateOffer}
                          className="bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm">
                          {item.status === "Active" ? <X size={13} /> : <RotateCcw size={13} />}
                        </button>
                        <button
                          onClick={() => {
                            setDiscountToDelete(item.discount_id);
                            setDeletePopup(true);
                          }}
                          title={t.delete}
                          className="bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-10 pb-4 text-xs text-custom-letters text-left border-t border-gray-100 pt-3 bg-white/30 animate-in fade-in slide-in-from-top-1 duration-200">
                        <p className="mb-1"><span className="font-semibold">{t.datePub}:</span> {new Date(item.created_at).toLocaleDateString()}</p>
                        <p className="mb-1"><span className="font-semibold">{t.status}:</span> <span className="text-custom-grad1">{getStatusLabel(item.status)}</span></p>
                        <p className="mb-1 flex items-center gap-1 min-w-0">
                          <span className="font-semibold shrink-0">{t.location}:</span>
                          <button
                            onClick={() => handleShowOnMap(item)}
                            className="text-custom-grad4 underline cursor-pointer flex items-center gap-1 truncate min-w-0"
                          >
                            <MapPin size={13} className="shrink-0" />
                            <span className="truncate">{item.store_name}</span>
                          </button>
                        </p>
                        <p><span className="font-semibold">{t.rating}:</span> {item.rating}/5</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div ref={loaderRef} className="h-10 w-full max-w-6xl" />
          {loadingMore && <div className="text-center mt-2">{t.loadMore}</div>}
          {!hasMore && !loading && discounts.length > 0 && (
            <div className="text-center mt-2 text-custom-letters">{t.nmDiscounts}</div>
          )}

          {deletePopup && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div role="dialog" aria-modal="true" aria-labelledby="mydiscounts-delete-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl animate-fadeIn text-center">

                <h3 id="mydiscounts-delete-title" className="text-lg font-semibold text-custom-letters mb-3">
                  {t.delTitle}
                </h3>

                <p className="text-sm text-custom-letters mb-6">
                  {t.delMsg}
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setDeletePopup(false)}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition"
                  >
                    {t.cancel}
                  </button>

                  <button
                    onClick={handleDelete}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition"
                  >
                    {t.delete}
                  </button>
                </div>

              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default MyDiscounts;